import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { CdcExtractionDetail, VaccineRecommendation, TravelNotice, MalariaData } from '@/types';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Globe,
  Shield,
  Bug,
  Syringe,
  Play,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
  not_found: { label: 'Not Found', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
};

const recTypeConfig: Record<string, { label: string; color: string }> = {
  required_entry: { label: 'Required', color: 'bg-red-100 text-red-800' },
  recommended_all: { label: 'Recommended (All)', color: 'bg-green-100 text-green-800' },
  recommended_some: { label: 'Recommended (Some)', color: 'bg-yellow-100 text-yellow-800' },
  conditional: { label: 'Conditional', color: 'bg-blue-100 text-blue-800' },
  not_recommended: { label: 'Not Recommended', color: 'bg-gray-100 text-gray-800' },
};

const riskConfig: Record<string, { color: string }> = {
  none: { color: 'text-gray-500' },
  low: { color: 'text-green-600' },
  moderate: { color: 'text-yellow-600' },
  high: { color: 'text-orange-600' },
  very_high: { color: 'text-red-600' },
};

export default function CdcExtractionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [extraction, setExtraction] = useState<CdcExtractionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isReextracting, setIsReextracting] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    travelNotices: true,
    vaccines: true,
    malaria: true,
    metadata: false,
  });
  const [expandedVaccines, setExpandedVaccines] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (id) fetchExtraction();
  }, [id]);

  const fetchExtraction = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await api.getCdcExtractionDetail(id!);
      setExtraction(data.extraction);
    } catch (err: any) {
      setError(err.message || 'Failed to load extraction');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReextract = async () => {
    if (!extraction) return;
    setIsReextracting(true);
    try {
      await api.triggerCdcExtraction(extraction.country_iso2);
      navigate('/cdc-extractions');
    } catch (err: any) {
      setError(err.message || 'Failed to trigger re-extraction');
    } finally {
      setIsReextracting(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleVaccine = (index: number) => {
    setExpandedVaccines((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !extraction) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="text-red-500" size={24} />
          <div>
            <h3 className="font-medium text-red-900">Error loading extraction</h3>
            <p className="text-red-700">{error || 'Extraction not found'}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/cdc-extractions')}
          className="mt-4 text-red-700 hover:text-red-800 font-medium"
        >
          &larr; Back to extractions
        </button>
      </div>
    );
  }

  const data = extraction.extracted_data;
  const status = statusConfig[extraction.extraction_status] || statusConfig.processing;
  const StatusIcon = status.icon;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/cdc-extractions')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to extractions
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full text-sm font-bold text-gray-700">
                {extraction.country_iso2}
              </span>
              <h1 className="text-2xl font-bold text-gray-900">{extraction.country_name}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                <StatusIcon size={14} className="mr-1" />
                {status.label}
              </span>
              {extraction.is_current && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Current
                </span>
              )}
              {extraction.validation_passed !== undefined && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  extraction.validation_passed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {extraction.validation_passed ? 'Validated' : 'Validation Issues'}
                </span>
              )}
              {extraction.cdc_url && (
                <a
                  href={extraction.cdc_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs text-accent-600 hover:text-accent-700"
                >
                  <ExternalLink size={12} className="mr-1" />
                  CDC Page
                </a>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-4 lg:mt-0">
            <button
              onClick={handleReextract}
              disabled={isReextracting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isReextracting ? (
                <Loader2 size={18} className="inline mr-2 animate-spin" />
              ) : (
                <Play size={18} className="inline mr-2" />
              )}
              Re-extract
            </button>
          </div>
        </div>
      </div>

      {/* Extraction Error */}
      {extraction.extraction_error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-red-600 flex-shrink-0" size={20} />
            <div>
              <h4 className="font-medium text-red-800">Extraction Error</h4>
              <p className="text-sm text-red-700 mt-1">{extraction.extraction_error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Warnings */}
      {extraction.validation_warnings && extraction.validation_warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
            <div>
              <h4 className="font-medium text-yellow-800">Validation Warnings</h4>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                {extraction.validation_warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {data ? (
        <div className="space-y-4">
          {/* Travel Notices */}
          {data.travel_notices && data.travel_notices.length > 0 && (
            <CollapsibleSection
              title={`Travel Notices (${data.travel_notices.length})`}
              icon={AlertTriangle}
              isOpen={expandedSections.travelNotices}
              onToggle={() => toggleSection('travelNotices')}
            >
              <div className="space-y-3">
                {data.travel_notices.map((notice, i) => (
                  <TravelNoticeCard key={i} notice={notice} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Vaccine Recommendations */}
          <CollapsibleSection
            title={`Vaccine Recommendations (${data.vaccine_recommendations?.length || 0})`}
            icon={Syringe}
            isOpen={expandedSections.vaccines}
            onToggle={() => toggleSection('vaccines')}
          >
            {data.vaccine_recommendations && data.vaccine_recommendations.length > 0 ? (
              <div className="space-y-2">
                {data.vaccine_recommendations.map((vax, i) => (
                  <VaccineCard
                    key={i}
                    vaccine={vax}
                    isExpanded={!!expandedVaccines[i]}
                    onToggle={() => toggleVaccine(i)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No vaccine recommendations extracted.</p>
            )}
          </CollapsibleSection>

          {/* Malaria */}
          {data.malaria && data.malaria.risk_exists && (
            <CollapsibleSection
              title="Malaria"
              icon={Bug}
              isOpen={expandedSections.malaria}
              onToggle={() => toggleSection('malaria')}
            >
              <MalariaSection malaria={data.malaria} />
            </CollapsibleSection>
          )}

          {/* Metadata */}
          <CollapsibleSection
            title="Extraction Metadata"
            icon={Shield}
            isOpen={expandedSections.metadata}
            onToggle={() => toggleSection('metadata')}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <MetaItem label="Model" value={extraction.llm_model_id || 'N/A'} />
              <MetaItem label="Input Tokens" value={extraction.llm_input_tokens?.toLocaleString() || 'N/A'} />
              <MetaItem label="Output Tokens" value={extraction.llm_output_tokens?.toLocaleString() || 'N/A'} />
              <MetaItem
                label="Latency"
                value={extraction.extraction_latency_ms ? `${(extraction.extraction_latency_ms / 1000).toFixed(1)}s` : 'N/A'}
              />
              <MetaItem label="Triggered By" value={extraction.triggered_by || 'N/A'} />
              <MetaItem label="Created" value={formatDate(extraction.created_at)} />
              <MetaItem label="Updated" value={formatDate(extraction.updated_at)} />
              <MetaItem label="Batch ID" value={extraction.batch_id ? extraction.batch_id.slice(0, 8) + '...' : 'N/A'} />
            </div>
            {extraction.raw_html_s3_key && (
              <div className="mt-4 text-xs text-gray-400">
                <p>S3 Raw: {extraction.raw_html_s3_key}</p>
                <p>S3 JSON: {extraction.extracted_json_s3_key}</p>
              </div>
            )}
          </CollapsibleSection>
        </div>
      ) : (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-500">No extracted data available</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function CollapsibleSection({
  title,
  icon: Icon,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  icon: any;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Icon size={18} className="text-gray-500" />
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function TravelNoticeCard({ notice }: { notice: TravelNotice }) {
  const levelColors: Record<number, string> = {
    1: 'border-blue-300 bg-blue-50',
    2: 'border-yellow-300 bg-yellow-50',
    3: 'border-orange-300 bg-orange-50',
    4: 'border-red-300 bg-red-50',
  };
  const levelTextColors: Record<number, string> = {
    1: 'text-blue-800',
    2: 'text-yellow-800',
    3: 'text-orange-800',
    4: 'text-red-800',
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-3 ${levelColors[notice.level] || 'border-gray-300 bg-gray-50'}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-bold ${levelTextColors[notice.level] || 'text-gray-800'}`}>
              Level {notice.level}
            </span>
            <span className="text-xs text-gray-500">{notice.level_description}</span>
          </div>
          <p className="font-medium text-gray-900 mt-1">{notice.title}</p>
          <p className="text-sm text-gray-600 mt-1">{notice.customer_summary}</p>
        </div>
        {notice.date && (
          <span className="text-xs text-gray-400 flex-shrink-0 ml-4">{notice.date}</span>
        )}
      </div>
    </div>
  );
}

function VaccineCard({
  vaccine,
  isExpanded,
  onToggle,
}: {
  vaccine: VaccineRecommendation;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const recType = recTypeConfig[vaccine.recommendation_type] || recTypeConfig.conditional;
  const risk = riskConfig[vaccine.risk_level] || riskConfig.moderate;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${recType.color}`}>
            {recType.label}
          </span>
          <span className="font-medium text-gray-900 truncate">{vaccine.disease_name}</span>
          <span className={`text-xs font-medium flex-shrink-0 ${risk.color}`}>
            {vaccine.risk_level?.toUpperCase()}
          </span>
        </div>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100 space-y-3">
          {vaccine.customer_summary && (
            <div className="mt-3 p-2 bg-primary-50 rounded text-sm text-primary-800">
              {vaccine.customer_summary}
            </div>
          )}

          {vaccine.who_should_get && (
            <div className="text-sm">
              <span className="text-gray-500">Who should get:</span>{' '}
              <span className="text-gray-700">{vaccine.who_should_get}</span>
            </div>
          )}

          {vaccine.included_vaccines && vaccine.included_vaccines.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-500">Includes:</span>{' '}
              <span className="text-gray-700">{vaccine.included_vaccines.join(', ')}</span>
            </div>
          )}

          {vaccine.entry_requirement && (
            <div className="text-sm bg-gray-50 p-2 rounded">
              <span className="font-medium text-gray-700">Entry Requirement:</span>{' '}
              {vaccine.entry_requirement.is_required_for_entry
                ? <span className="text-red-700">Required for entry</span>
                : <span className="text-gray-600">Not required for entry</span>}
              {vaccine.entry_requirement.conditional_entry && (
                <span className="text-yellow-700 ml-1">(conditional from risk countries)</span>
              )}
            </div>
          )}

          {vaccine.area_specific?.is_area_specific && (
            <div className="text-sm bg-gray-50 p-2 rounded space-y-1">
              <span className="font-medium text-gray-700">Area-Specific:</span>
              {vaccine.area_specific.recommended_areas && vaccine.area_specific.recommended_areas.length > 0 && (
                <p className="text-gray-600">
                  <span className="text-green-700">Recommended:</span>{' '}
                  {vaccine.area_specific.recommended_areas.join(', ')}
                </p>
              )}
              {vaccine.area_specific.not_recommended_areas && vaccine.area_specific.not_recommended_areas.length > 0 && (
                <p className="text-gray-600">
                  <span className="text-red-700">Not recommended:</span>{' '}
                  {vaccine.area_specific.not_recommended_areas.join(', ')}
                </p>
              )}
            </div>
          )}

          {vaccine.special_populations && Object.keys(vaccine.special_populations).length > 0 && (
            <div className="text-sm bg-yellow-50 p-2 rounded">
              <span className="font-medium text-yellow-800">Special Populations:</span>
              <ul className="mt-1 space-y-1">
                {Object.entries(vaccine.special_populations).map(([key, val]) => (
                  <li key={key} className="text-yellow-700">
                    <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {val}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vaccine.disease_specific && Object.keys(vaccine.disease_specific).length > 0 && (
            <div className="text-sm bg-blue-50 p-2 rounded">
              <span className="font-medium text-blue-800">Disease Details:</span>
              <ul className="mt-1 space-y-0.5">
                {Object.entries(vaccine.disease_specific).map(([key, val]) => (
                  <li key={key} className="text-blue-700">
                    <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                    {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : Array.isArray(val) ? val.join(', ') : String(val)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vaccine.clinical_guidance_summary && (
            <div className="text-sm">
              <span className="text-gray-500">Clinical guidance:</span>{' '}
              <span className="text-gray-700">{vaccine.clinical_guidance_summary}</span>
            </div>
          )}

          {vaccine.cdc_raw_text && (
            <details className="text-xs">
              <summary className="text-gray-400 cursor-pointer hover:text-gray-600">CDC raw text</summary>
              <p className="mt-1 text-gray-500 whitespace-pre-wrap">{vaccine.cdc_raw_text}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function MalariaSection({ malaria }: { malaria: MalariaData }) {
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-gray-50 p-3 rounded">
          <span className="text-gray-500">Risk Level</span>
          <p className={`font-medium ${riskConfig[malaria.risk_level || 'moderate']?.color || 'text-gray-900'}`}>
            {malaria.risk_level?.toUpperCase() || 'N/A'}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <span className="text-gray-500">Prophylaxis</span>
          <p className="font-medium text-gray-900">{malaria.prophylaxis_recommended ? 'Recommended' : 'Not Recommended'}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <span className="text-gray-500">Transmission</span>
          <p className="font-medium text-gray-900">{malaria.transmission_type?.replace(/_/g, ' ') || 'N/A'}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <span className="text-gray-500">Season</span>
          <p className="font-medium text-gray-900">{malaria.transmission_season || 'N/A'}</p>
        </div>
      </div>

      {/* Species */}
      {malaria.species && malaria.species.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Species</h4>
          <div className="flex flex-wrap gap-2">
            {malaria.species.map((sp, i) => (
              <span key={i} className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-800 text-sm rounded-full">
                <em>{sp.name}</em>
                {sp.prevalence && <span className="ml-1 font-medium">({sp.prevalence})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Drug Resistance */}
      {malaria.drug_resistance && malaria.drug_resistance.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-red-700 mb-2">Drug Resistance</h4>
          <div className="flex flex-wrap gap-2">
            {malaria.drug_resistance.map((drug, i) => (
              <span key={i} className="inline-flex px-2 py-1 bg-red-50 text-red-700 text-sm rounded">
                {drug}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Drugs */}
      {malaria.recommended_drugs && malaria.recommended_drugs.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-green-700 mb-2">Recommended Prophylaxis</h4>
          <div className="flex flex-wrap gap-2">
            {malaria.recommended_drugs.map((drug, i) => (
              <span key={i} className="inline-flex px-2 py-1 bg-green-50 text-green-700 text-sm rounded">
                {drug}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Areas */}
      {malaria.areas && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Areas</h4>
          <div className="space-y-2 text-sm">
            {malaria.areas.high_risk && malaria.areas.high_risk.length > 0 && (
              <div>
                <span className="font-medium text-red-700">High risk:</span>{' '}
                <span className="text-gray-700">{malaria.areas.high_risk.join(', ')}</span>
              </div>
            )}
            {malaria.areas.moderate_risk && malaria.areas.moderate_risk.length > 0 && (
              <div>
                <span className="font-medium text-orange-700">Moderate risk:</span>{' '}
                <span className="text-gray-700">{malaria.areas.moderate_risk.join(', ')}</span>
              </div>
            )}
            {malaria.areas.low_risk && malaria.areas.low_risk.length > 0 && (
              <div>
                <span className="font-medium text-yellow-700">Low risk:</span>{' '}
                <span className="text-gray-700">{malaria.areas.low_risk.join(', ')}</span>
              </div>
            )}
            {malaria.areas.no_risk && malaria.areas.no_risk.length > 0 && (
              <div>
                <span className="font-medium text-green-700">No risk:</span>{' '}
                <span className="text-gray-700">{malaria.areas.no_risk.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Summary */}
      {malaria.customer_summary && (
        <div className="p-3 bg-primary-50 rounded text-sm text-primary-800">
          {malaria.customer_summary}
        </div>
      )}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-gray-500">{label}</span>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  );
}

function formatDate(dateString?: string) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
